// Canvas layout model: a canvas renders as ROWS of CELLS.
//
//   rows: [ { cells: [ { kernel_id?, command, caption?, parameters?, width? } ] } ]
//
// Bundled cells carry a stable kernel UUID plus a temporary command mirror;
// legacy/user cells may still be command-only. Cells sharing a row divide the
// row's width by their `width` weight (default 1, i.e. equal columns). Canvases
// authored before this model stored a flat `items: [{ command, caption }]`;
// those upgrade to one cell per row so the historical stacked layout renders
// unchanged. `normalizeRows` is the single reader every surface (renderer,
// editor, store, dev guards) goes through, so both shapes render identically
// and no one-time migration pass is required.

export function normalizeCell(raw) {
    if (!raw || typeof raw.command !== "string" || !raw.command.trim()) return null;
    const cell = { command: raw.command.trim() };
    if (typeof raw.kernel_id === "string" && raw.kernel_id.trim()) {
        cell.kernel_id = raw.kernel_id.trim();
    }
    if (typeof raw.caption === "string" && raw.caption.trim()) {
        cell.caption = raw.caption.trim();
    }
    if (raw.parameters && typeof raw.parameters === "object" && !Array.isArray(raw.parameters)) {
        cell.parameters = { ...raw.parameters };
    }
    const w = Number(raw.width);
    // Only carry a weight when it actually differs from the default track.
    if (Number.isFinite(w) && w > 0 && w !== 1) cell.width = w;
    return cell;
}

// A width is a relative weight between cells in the same row. Once a row has
// only one cell there is no sibling boundary to size, so carrying its old
// weight would strand invisible, uneditable layout state.
export function normalizeSingletonWidths(rows) {
    return (rows || []).map((row) => {
        if (row?.cells?.length !== 1 || row.cells[0].width === undefined) return row;
        const cell = { ...row.cells[0] };
        delete cell.width;
        return { ...row, cells: [cell] };
    });
}

// Read a canvas's layout as normalized rows, accepting either the current
// `rows` shape or a legacy flat `items` array. Returns fresh objects (safe to
// hand to a fork without sharing structure with the source).
export function normalizeRows(canvas) {
    if (Array.isArray(canvas?.rows)) {
        return normalizeSingletonWidths(canvas.rows
            .map((row) => ({
                cells: (Array.isArray(row?.cells) ? row.cells : [])
                    .map(normalizeCell)
                    .filter(Boolean),
            }))
            .filter((row) => row.cells.length > 0));
    }
    if (Array.isArray(canvas?.items)) {
        return normalizeSingletonWidths(canvas.items
            .map(normalizeCell)
            .filter(Boolean)
            .map((cell) => ({ cells: [cell] })));
    }
    return [];
}

// Every cell across every row, in reading order. For reverse-lookups
// ("which canvases use kernel X") and counts.
export function flattenCells(canvas) {
    return normalizeRows(canvas).flatMap((row) => row.cells);
}

export function cellCount(canvas) {
    return flattenCells(canvas).length;
}

// Render kinds that must never share a row. A treemap (and other space-filling
// packings) reads as a tall sliver in a narrow column, so it always claims a
// full-width row of its own. Extend this set if another packing view proves to
// need the same treatment.
export const SOLO_ROW_KINDS = new Set(["treemap", "composition"]);

// Guarantee the solo-kind rule regardless of how a canvas was authored: split
// any row so that a solo-kind cell sits alone on a full-width row. Non-solo
// cells keep their grouping and reading order. `kindOf` maps a cell's command
// to its kernel render kind (or undefined when the kernel is missing). This is
// a render-layer pass — it needs kernel definitions the layout data doesn't
// carry — so callers thread `kindOf` in from their kernel map.
export function enforceSoloRows(rows, kindOf) {
    const out = [];
    for (const row of rows || []) {
        let group = null;
        for (const cell of row?.cells || []) {
            if (SOLO_ROW_KINDS.has(kindOf?.(cell.command, cell))) {
                group = null;
                out.push({ cells: [cell] });
            } else {
                if (!group) { group = { cells: [] }; out.push(group); }
                group.cells.push(cell);
            }
        }
    }
    return normalizeSingletonWidths(out);
}

// grid-template-columns for one row: a min-0 track per cell, sized by its
// `width` weight. minmax(0, Nfr) is the grid analogue of the flex `min-w-0`
// trick — it pins the track so a self-measuring widget (e.g. a treemap) can't
// grow its own column wider every frame.
export function rowGridColumns(row) {
    const cells = row?.cells || [];
    if (!cells.length) return "minmax(0, 1fr)";
    return cells
        .map((c) => `minmax(0, ${c.width && c.width > 0 ? c.width : 1}fr)`)
        .join(" ");
}

// Interactive canvases use wrapping rows so a control-rich card never gets
// squeezed below a readable width. Existing v1 canvases keep the grid path.
export function runtimeRowCellStyle(cell, runtimeCell) {
    const minimum = runtimeCell?.minimumWidth || 320;
    const weight = cell?.width && cell.width > 0 ? cell.width : 1;
    return {
        flexGrow: weight,
        flexShrink: 1,
        flexBasis: `${minimum}px`,
        minWidth: `min(${minimum}px, 100%)`,
    };
}

// ── WYSIWYG editor ops ────────────────────────────────────────────────────

export function rowFractions(row) {
    const cells = row?.cells || [];
    const weights = cells.map((c) => (c.width && c.width > 0 ? c.width : 1));
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    return weights.map((w) => w / total);
}

function cellIdentity(cell) {
    return cell?.kernel_id || cell?.command;
}

export function moveCell(rows, identity, target, kindOf) {
    const src = findCell(rows, identity);
    if (!src || !target) return rows;
    const srcCell = rows[src.row].cells[src.cell];
    if (target.type === "cell") {
        if (SOLO_ROW_KINDS.has(kindOf?.(srcCell.command, srcCell))) return rows;
        const destRow = rows[target.row];
        if (!destRow) return rows;
        if (rows[src.row].cells.length === 1 && target.row === src.row) return rows;
        const soloInDest = destRow.cells.some(
            (cell) => cellIdentity(cell) !== identity
                && SOLO_ROW_KINDS.has(kindOf?.(cell.command, cell)),
        );
        if (soloInDest) return rows;
    }

    const moved = { ...srcCell };
    delete moved.width;
    const without = normalizeSingletonWidths(rows
        .map((row, ri) => (ri === src.row
            ? { cells: row.cells.filter((_, ci) => ci !== src.cell) }
            : { cells: [...row.cells] }))
        .filter((row) => row.cells.length > 0));
    const srcRowRemoved = rows[src.row].cells.length === 1;

    if (target.type === "row") {
        let at = target.index;
        if (srcRowRemoved && src.row < target.index) at -= 1;
        at = Math.max(0, Math.min(without.length, at));
        return [...without.slice(0, at), { cells: [moved] }, ...without.slice(at)];
    }
    let destRow = target.row;
    if (srcRowRemoved && src.row < target.row) destRow -= 1;
    if (destRow < 0 || destRow >= without.length) return rows;
    let at = target.index;
    if (!srcRowRemoved && destRow === src.row && src.cell < at) at -= 1;
    const cells = without[destRow].cells;
    at = Math.max(0, Math.min(cells.length, at));
    without[destRow] = { cells: [...cells.slice(0, at), moved, ...cells.slice(at)] };
    return without;
}

function findCell(rows, identity) {
    for (let ri = 0; ri < (rows?.length || 0); ri += 1) {
        const ci = rows[ri].cells.findIndex((cell) => cellIdentity(cell) === identity);
        if (ci !== -1) return { row: ri, cell: ci };
    }
    return null;
}

const WIDTH_SNAP = 0.05;
const WIDTH_MIN = 0.15;

export function applyDividerDrag(row, boundary, deltaFraction) {
    const cells = row?.cells || [];
    if (boundary < 0 || boundary >= cells.length - 1) return row;
    const fractions = rowFractions(row);
    const combined = fractions[boundary] + fractions[boundary + 1];
    if (combined < WIDTH_MIN * 2) return row;
    let left = Math.round((fractions[boundary] + deltaFraction) / WIDTH_SNAP) * WIDTH_SNAP;
    left = Math.max(WIDTH_MIN, Math.min(combined - WIDTH_MIN, left));
    if (Math.abs(left - fractions[boundary]) < 1e-9) return row;
    const nextFractions = [...fractions];
    nextFractions[boundary] = left;
    nextFractions[boundary + 1] = combined - left;
    const n = cells.length;
    return {
        cells: cells.map((cell, i) => {
            const weight = Math.round(nextFractions[i] * n * 1e4) / 1e4;
            const next = { ...cell };
            delete next.width;
            if (Math.abs(weight - 1) > 1e-9) next.width = weight;
            return next;
        }),
    };
}

export function resetRowWidths(row) {
    if (!row?.cells?.some((c) => c.width !== undefined)) return row;
    return {
        cells: row.cells.map((cell) => {
            const next = { ...cell };
            delete next.width;
            return next;
        }),
    };
}
